import { Component, Input, OnInit, AfterViewInit, ViewChild, TemplateRef, ViewEncapsulation } from '@angular/core';
import { NgbActiveModal, NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { MasterService } from 'app/service/master.service';

@Component({
  selector: 'app-bulk-upload-preview-modal',
  templateUrl: './bulk-upload-preview-modal.component.html',
  styleUrls: [
    './bulk-upload-preview-modal.component.scss',
    '../ingredient-bulk-edit-preview-modal/ingredient-bulk-edit-preview-modal.component.scss',
  ],
  encapsulation: ViewEncapsulation.None,
})
export class BulkUploadPreviewModalComponent implements OnInit, AfterViewInit {

  @Input() parsedIngredients: any[] = [];
  
  dataSourceOptions: any[] = [];
  foodGroups: any[] = [];
  foodGroupsFAO: any[] = [];
  seasons: any[] = [];
  nutrientGroups: any[] = [];
  nutrientTags: any[] = [];
  allergensContains: any[] = [];
  allergensFree: any[] = [];
  intolerancesContains: any[] = [];
  intolerancesFree: any[] = [];
  categories: any[] = [];
  mealTypes: any[] = [];
  occasions: any[] = [];
  mealPreferences: any[] = [];
  dietaryPreferenceOptions: Array<{ id: string; name: string }> = [];
  healthColorCodeOptions: Array<{ id: number; name: string }> = [];
  rawMaterialTypeOptions: Array<{ id: any; name: string }> = [];
  glycemicIndexOptions: Array<{ id: string; name: string; code: number }> = [];
  statusOptions: Array<{ value: string; label: string }> = [
    { value: 'Active', label: 'Published' },
    { value: 'Draft', label: 'Draft' },
  ];
  /** Header control — applies Draft/Published to every row in the batch. */
  bulkStatus: string = 'Draft';
  stateOfProductOptions: Array<{ value: string; label: string }> = [
    { value: 'Solid', label: 'Solid (per 100g)' },
    { value: 'Liquid', label: 'Liquid (per 100ml)' },
  ];

  /** Avoid rendering 500 full cards (browser hang). */
  previewPage = 1;
  readonly previewPageSize = 10;

  /** Cache nutrient tags by group id — avoid N identical getNutrientTagsByGroupId calls for bulk rows. */
  private readonly nutrientTagsByGroupId = new Map<string, Array<{ tagId: any; tagName: string }>>();
  private readonly nutrientTagsByGroupIdInflight = new Map<string, Promise<Array<{ tagId: any; tagName: string }>>>();

  private readonly masterAliases: Record<string, string[]> = {
    vegetarian: ['vegetarian', 'lacto vegetarian', 'ovo vegetarian'],
    veg: ['veg'],
    'non veg': ['non veg', 'nonvegetarian', 'non vegetarian', 'non-veg', 'nonveg'],
    vegan: ['vegan'],
    eggetarian: ['eggetarian', 'eggitarian'],
    'all season': ['all season', 'all seasons', 'all year', 'all year round', 'year round'],
    amber: ['amber', 'orange', 'yellow', 'orange brown', 'orange brown color code', 'yellow color code', 'light yellow beige color code'],
    green: ['green', 'green color code'],
    red: ['red', 'red pink', 'red pink color code'],
    'food raw material': ['food raw material', 'raw material', 'raw food'],
    cereals: ['cereal', 'cereals', 'cereals and cereal products', 'cereal products'],
    'daily food use': ['daily food use', 'daily food', 'everyday', 'regular'],
  };

  @ViewChild('micronutrientsModal') micronutrientsModal: TemplateRef<any>;
  @ViewChild('uomModal') uomModal: TemplateRef<any>;
  @ViewChild('nutrientTagsModal') nutrientTagsModal: TemplateRef<any>;
  selectedMicronutrientsItem: any = null;
  selectedMicronutrientsList: any[] = [];
  selectedUomItem: any = null;
  selectedNutrientTagsItem: any = null;
  selectedNutrientTagOptions: any[] = [];
  allNutrientTagOptionsForModal: any[] = [];
  nutrientTagToAdd: any = null;
  
  public sizeOptions = [
    { name: 'Small', value: 'Small' },
    { name: 'Medium', value: 'Medium' },
    { name: 'Large', value: 'Large' },
    { name: '1/4', value: '1/4' },
    { name: '1/2', value: '1/2' },
    { name: '1/3', value: '1/3' },
    { name: '3/4', value: '3/4' },
    { name: '1', value: '1' },
    { name: '2', value: '2' },
    { name: '3', value: '3' }
  ];

  public unitOptions: Array<{ name: string; value: string }> = [];
  public servingSizeOptions: Array<{ name: string; value: string }> = [];
  public servingMeasureOptions: Array<{ name: string; value: string }> = [];
  private masterServingUnits: any[] = [];
  
  constructor(public activeModal: NgbActiveModal, private modalService: NgbModal, private masterService: MasterService) { }

  private isAllSeasonsOption(option: any): boolean {
    const normalizedName = String(option?.name || '')
      .trim()
      .toLowerCase()
      .replace(/[-_]+/g, ' ')
      .replace(/\s+/g, ' ');
    return normalizedName === 'all season'
      || normalizedName === 'all seasons'
      || normalizedName === 'all year round';
  }

  private expandAllSeasons(ids: any[]): any[] {
    const normalizedIds = Array.from(new Set((ids || []).filter(id => id !== null && id !== undefined && id !== '')));
    const hasAllSeasons = this.seasons.some(option =>
      this.isAllSeasonsOption(option)
      && normalizedIds.some(id => String(id) === String(option.id))
    );
    return hasAllSeasons ? this.seasons.map(option => option.id) : normalizedIds;
  }

  private resolveSeasonIds(item: any): any[] {
    const rawSeasonValues = Array.isArray(item.seasonIds) && item.seasonIds.length > 0
      ? item.seasonIds
      : String(item.seasonId ?? '')
          .split(/[|,;]/)
          .map(value => value.trim())
          .filter(value => !!value);
    const resolvedIds = rawSeasonValues
      .map((rawValue: any) => {
        const value = String(rawValue?.id ?? rawValue).trim();
        const matched = this.findMasterOption(this.seasons, value);
        return matched?.id ?? null;
      })
      .filter((id: any) => id !== null && id !== undefined);
    return this.expandAllSeasons(resolvedIds);
  }

  public onSeasonSelectionChange(item: any, value: any): void {
    const previousIds = Array.isArray(item.seasonIds) ? item.seasonIds : [];
    const nextIds = Array.isArray(value) ? value : [];
    const allSeasonIds = this.seasons
      .filter(option => this.isAllSeasonsOption(option))
      .map(option => String(option.id));
    const hadAllSeasons = previousIds.some(id => allSeasonIds.includes(String(id)));
    const hasAllSeasons = nextIds.some(id => allSeasonIds.includes(String(id)));

    item.seasonIds = hasAllSeasons && !hadAllSeasons
      ? this.seasons.map(option => option.id)
      : hadAllSeasons && hasAllSeasons && nextIds.length < previousIds.length
        ? nextIds.filter(id => !allSeasonIds.includes(String(id)))
        : nextIds;
    item.seasonId = item.seasonIds[0] ?? null;
  }

  async ngOnInit(): Promise<void> {
    await this.loadMasterData();

    const items = this.parsedIngredients || [];
    // Normalize all rows locally first (no per-row HTTP — recipe-bulk style).
    for (const item of items) {
      this.normalizeItemFromCsv(item);
      this.mapMacrosToItem(item);
    }

    // Prefetch unique nutrient-group tags once, then apply to every row that needs them.
    const groupIds = new Set<string>();
    for (const item of items) {
      (item.nutrientGroups || []).forEach((g: any) => {
        const id = g?.nutrientGroupId ?? g?.id;
        if (id != null && id !== '') groupIds.add(String(id));
      });
    }
    if (groupIds.size) {
      await Promise.all(Array.from(groupIds).map((id) => this.getNutrientTagsForGroupCached(id)));
    }

    // Apply tags synchronously after cache warm — avoid 500 sequential awaits
    for (const item of items) {
      if (item.nutrientGroups?.length) {
        void this.autoSelectNutrientTagsForItem(item);
      }
    }

    this.scheduleLucideIconRender();
  }

  /** Map CSV name strings → master IDs; unmatched dropdown values are ignored (left blank). */
  private normalizeItemFromCsv(item: any): void {
    if (!item.allergens) item.allergens = { contains: [], free: [] };
    if (!item.allergens.contains) item.allergens.contains = [];
    if (!item.allergens.free) item.allergens.free = [];
    if (!item.intolerances) item.intolerances = { contains: [], free: [] };
    if (!item.intolerances.contains) item.intolerances.contains = [];
    if (!item.intolerances.free) item.intolerances.free = [];
    if (!Array.isArray(item.ingredientUnitOfMeasurements)) item.ingredientUnitOfMeasurements = [];
    // Bulk upload always opens as Draft — use header control to publish all at once
    item.status = 'Draft';
    if (!item.brandName && item.BrandName) item.brandName = item.BrandName;
    if (item.glycemicIndex != null && item.glycemicIndex !== '') {
      item.glycemicIndex = this.mapGlycemicIndexToCategory(item.glycemicIndex);
    }

    // Normalize UOMs from CSV/XLSX parse (PascalCase or already camelCase)
    const rawUoms = Array.isArray(item.ingredientUnitOfMeasurements) && item.ingredientUnitOfMeasurements.length
      ? item.ingredientUnitOfMeasurements
      : (Array.isArray(item.IngredientUnitOfMeasurements) ? item.IngredientUnitOfMeasurements : []);
    item.ingredientUnitOfMeasurements = (rawUoms || [])
      .map((u: any) => {
        if (!u) return null;
        const amount = Number(u.quantity ?? u.Quantity ?? u.valueInGram ?? u.ValueInGram ?? 0) || 0;
        let size = String(u.size ?? u.Size ?? '').trim();
        let measure = this.normalizeMeasureValue(u.unit ?? u.Unit);
        let unitType = this.normalizeUnitValue(u.type ?? u.Type ?? 'g') || 'g';

        // Skip bogus packed cells like "100 g" parsed as size-only with amount 0
        // when measure is empty and amount is 0 but size looks like "100 g"
        if (!measure && amount <= 0 && /^\d+(\.\d+)?\s*[a-zA-Z]+$/.test(size)) {
          return null;
        }
        // If size is "100 g" style but amount empty, parse it
        if ((!amount || amount <= 0) && /^\d+(\.\d+)?\s*[a-zA-Z]+$/.test(size)) {
          const m = size.match(/^(\d+(?:\.\d+)?)\s*([a-zA-Z]+)$/);
          if (m) {
            size = m[1];
            unitType = this.normalizeUnitValue(m[2]) || unitType;
          }
        }
        if (!size && amount > 0) size = String(amount);

        this.ensureOptionPresent(this.servingSizeOptions, size);
        this.ensureOptionPresent(this.servingMeasureOptions, measure);
        this.ensureOptionPresent(this.unitOptions, unitType);

        return {
          size: size || null,
          unit: measure || null,
          type: unitType,
          quantity: amount,
          valueInGram: amount,
          isPreferred: !!(u.isPreferred ?? u.IsPreferred),
          isActive: u.isActive !== false && u.IsActive !== false,
          isDeleted: !!(u.isDeleted ?? u.IsDeleted),
          servingUnitMasterId: u.servingUnitMasterId ?? u.ServingUnitMasterId ?? null,
        };
      })
      .filter((u: any) => u && (u.size || u.unit || u.type || u.quantity));
    for (const uom of item.ingredientUnitOfMeasurements) {
      uom.servingUnitMasterId = this.resolveServingUnitMasterId(uom);
    }
    if (item.ingredientUnitOfMeasurements.length && !item.ingredientUnitOfMeasurements.some((u: any) => u.isPreferred)) {
      item.ingredientUnitOfMeasurements[0].isPreferred = true;
    }

    // State of Product (Solid / Liquid) — FSSAI cut-off basis
    const sopRaw = String(item.stateOfProduct ?? item.StateOfProduct ?? '').trim();
    const sopLower = sopRaw.toLowerCase();
    if (sopLower === 'liquid' || sopLower === 'wet' || sopLower === 'fluid') item.stateOfProduct = 'Liquid';
    else if (
      sopLower === 'solid'
      || sopLower === 'dry'
      || sopLower === 'raw'
      || sopLower === 'fresh'
      || !sopRaw
    ) item.stateOfProduct = 'Solid';
    else item.stateOfProduct = sopRaw;

    // Image URL from file
    const img = item.imageUrl || item.ImageUrl || item.imagePath || item.ImagePath || '';
    if (img && !item.imagePath) {
      item.imagePath = String(img).trim();
      item.imagePreview = item.imagePath;
    }

    // Nutrient information (macros + micros) — normalize casing from API
    const rawNutrients = Array.isArray(item.nutrientInformation) && item.nutrientInformation.length
      ? item.nutrientInformation
      : (Array.isArray(item.NutrientInformation) ? item.NutrientInformation : []);
    item.nutrientInformation = (rawNutrients || []).map((n: any) => ({
      nutrient: n.nutrient ?? n.Nutrient ?? n.nutrition ?? '',
      nutrientType: n.nutrientType ?? n.NutrientType ?? n.groupName ?? 'Micronutrient',
      unit: n.unit ?? n.Unit ?? n.measurement ?? 'g',
      amount: Number(n.amount ?? n.Amount ?? n.value ?? 0) || 0,
      nutrientParamId: n.nutrientParamId ?? n.NutrientParamId ?? null,
    })).filter((n: any) => n.nutrient);

    // FSSAI nutrient tag names from file (display; recalculated on save)
    const rawFssai = item.fssaiNutrientTagNames ?? item.FssaiNutrientTagNames ?? item.generatedFssaiNutrientTags ?? [];
    if (Array.isArray(rawFssai)) {
      item.fssaiNutrientTagNames = rawFssai
        .map((t: any) => typeof t === 'string' ? t.trim() : String(t?.fssaiTagName ?? t?.name ?? t?.FssaiTagName ?? '').trim())
        .filter((t: string) => !!t);
    } else if (typeof rawFssai === 'string') {
      item.fssaiNutrientTagNames = this.splitCsvParts(rawFssai);
    } else {
      item.fssaiNutrientTagNames = [];
    }

    const unresolved: Record<string, string> = {};

    const rawDataSource = String(item.dataSource ?? '').trim();
    const dsMatch = rawDataSource
      ? this.findMasterOption(this.dataSourceOptions, rawDataSource)
      : null;
    if (rawDataSource && !dsMatch) unresolved.dataSource = rawDataSource;
    item.dataSource = dsMatch ? dsMatch.id : null;

    const rawFoodGroup = item.foodGroupIds ?? item.foodGroupId ?? item.foodGroupName ?? '';
    const foodGroupRawText = this.rawCsvText(rawFoodGroup);
    item.foodGroupIds = this.resolveMasterIds(this.foodGroups, rawFoodGroup);
    item.foodGroupId = item.foodGroupIds[0] ?? null;
    if (foodGroupRawText && !item.foodGroupIds.length) unresolved.foodGroup = foodGroupRawText;

    const rawFao = item.foodGroupFAOIds ?? item.foodGroupFAOId ?? item.FoodGroupFAOIds ?? '';
    const faoRawText = this.rawCsvText(rawFao);
    item.foodGroupFAOIds = this.resolveMasterIds(this.foodGroupsFAO, rawFao);
    item.foodGroupFAOId = item.foodGroupFAOIds[0] ?? null;
    if (faoRawText && !item.foodGroupFAOIds.length) unresolved.foodGroupFAO = faoRawText;

    const seasonRaw = item.seasonIds ?? item.seasonId ?? item.SeasonId ?? item.seasonNames ?? '';
    const seasonRawText = this.rawCsvText(seasonRaw);
    item.seasonIds = this.resolveSeasonIds({ seasonIds: this.splitCsvParts(seasonRaw), seasonId: null });
    item.seasonId = item.seasonIds[0] ?? null;
    if (seasonRawText && !item.seasonIds.length) unresolved.season = seasonRawText;

    const catRaw = item.categoryNames ?? item.CategoryNames ?? item.categoryIds ?? item.category ?? item.itemCategoryId;
    const catRawText = this.rawCsvText(catRaw);
    item.categoryIds = this.resolveMasterIds(this.categories, catRaw);
    item.itemCategoryId = item.categoryIds[0] ?? null;
    item.category = item.itemCategoryId != null
      ? (this.categories.find((c: any) => String(c.id) === String(item.itemCategoryId))?.name || '')
      : '';
    if (catRawText && !item.categoryIds.length) unresolved.category = catRawText;

    const mealTypeRaw = item.mealTypeNames ?? item.MealTypeNames ?? item.mealTypeIds ?? item.mealType;
    const mealTypeRawText = this.rawCsvText(mealTypeRaw);
    item.mealTypeIds = this.resolveMasterIds(this.mealTypes, mealTypeRaw);
    item.mealType = item.mealTypeIds[0] ?? null;
    if (mealTypeRawText && !item.mealTypeIds.length) unresolved.mealType = mealTypeRawText;

    const occasionRaw = item.occasionNames ?? item.OccasionNames ?? item.occasionIds ?? item.occasionId;
    const occasionRawText = this.rawCsvText(occasionRaw);
    item.occasionIds = this.resolveMasterIds(this.occasions, occasionRaw);
    if (occasionRawText && !item.occasionIds.length) unresolved.occasion = occasionRawText;

    const mealPrefTaxRaw = item.mealPreferenceNames ?? item.MealPreferenceNames ?? item.mealPreferenceIds ?? item.mealPreferenceId;
    const mealPrefTaxRawText = this.rawCsvText(mealPrefTaxRaw);
    item.mealPreferenceIds = this.resolveMasterIds(this.mealPreferences, mealPrefTaxRaw);
    if (mealPrefTaxRawText && !item.mealPreferenceIds.length) unresolved.mealPreferenceTaxonomy = mealPrefTaxRawText;

    const dietaryRaw = String(
      item.mealPreference ?? item.dietaryPreference ?? item.DietaryPreference ?? ''
    ).trim();
    const dietaryName = this.resolveDietaryPreferenceName(dietaryRaw);
    if (dietaryRaw && !dietaryName) unresolved.dietaryPreference = dietaryRaw;
    item.mealPreference = dietaryName || '';

    const hccRaw = String(
      item.healthColorCodeName ?? item.HealthColorCodeName ?? item.healthColorCode ?? item.HealthColorCode ?? ''
    ).trim();
    const hccId = this.resolveHealthColorCodeId(hccRaw);
    if (hccRaw && hccId == null) unresolved.healthColorCode = hccRaw;
    item.healthColorCodeId = hccId;

    const rawTypeRaw = String(
      item.materialTypeName ?? item.MaterialTypeName ?? item.rawMaterialTypeName ?? item.RawMaterialTypeName ?? item.rawTypeName ?? item.rawTypeId ?? ''
    ).trim();
    const rawTypeMatch = rawTypeRaw
      ? this.rawMaterialTypeOptions.find((r) =>
          String(r.id) === rawTypeRaw || this.labelsMatch(r.name, rawTypeRaw)
        )
      : null;
    if (rawTypeRaw && !rawTypeMatch) unresolved.rawMaterialType = rawTypeRaw;
    item.rawTypeId = rawTypeMatch?.id ?? null;

    item.allergens.contains = this.resolveClaimObjects(this.allergensContains, item.allergens.contains);
    item.allergens.free = this.resolveClaimObjects(this.allergensFree, item.allergens.free);
    item.intolerances.contains = this.resolveClaimObjects(this.intolerancesContains, item.intolerances.contains);
    item.intolerances.free = this.resolveClaimObjects(this.intolerancesFree, item.intolerances.free);

    // Capture CSV nutrient tag names (if any) before resolving groups.
    item._csvNutrientTagNames = this.collectCsvNutrientTagNames(item);
    item.nutrientGroups = this.resolveNutrientGroups(item.nutrientGroups);

    if (typeof item.aliases === 'string') {
      // keep string for text input
    } else if (Array.isArray(item.aliases)) {
      item.aliases = item.aliases.join(', ');
    }
    if (Array.isArray(item.commonName)) {
      item.commonName = item.commonName.join(', ');
    } else if (Array.isArray(item.commonNames)) {
      item.commonName = item.commonNames.join(', ');
    }
    if (Array.isArray(item.regionalNames)) {
      item.regionalNames = item.regionalNames.join(', ');
    }

    item._csvUnresolved = unresolved;

    // Do NOT call nutrition-params here. That API is per existing ingredient id;
    // using dataSource id fired hundreds of pending requests on bulk upload.
    // Micronutrients come from the CSV/XLSX (and can be edited in preview).
  }

  private rawCsvText(raw: any): string {
    if (raw == null || raw === '') return '';
    if (Array.isArray(raw)) {
      return raw.map((v) => String(v?.name ?? v?.Name ?? v ?? '').trim()).filter(Boolean).join(' | ');
    }
    return String(raw).trim();
  }

  private splitCsvParts(raw: any): string[] {
    if (Array.isArray(raw)) {
      return raw.map((v) => String(v?.name ?? v?.Name ?? v ?? '').trim()).filter(Boolean);
    }
    return String(raw ?? '')
      .split(/[|,;]/)
      .map((s) => s.trim())
      .filter(Boolean);
  }

  private normalizeLabel(value: any): string {
    return String(value || '')
      .toLowerCase()
      .replace(/&/g, ' and ')
      .replace(/[^a-z0-9]+/g, ' ')
      .replace(/\s+/g, ' ')
      .trim();
  }

  /** Expand CSV labels via known aliases so masters match despite naming drift. */
  private expandMatchKeys(value: any): string[] {
    const base = this.normalizeLabel(value);
    if (!base) return [];
    const keys = new Set<string>([base]);
    const isNonVeg = /\bnon[\s-]?veg/.test(base) || base === 'nonvegetarian' || base === 'nonveg';
    const isVegFamily = !isNonVeg && (base === 'veg' || base === 'vegetarian' || /\bvegetarian\b/.test(base));

    // Explicit diet families — never cross-link Veg ↔ Non Veg
    if (isNonVeg) {
      ['non veg', 'nonvegetarian', 'non vegetarian', 'non-veg', 'nonveg'].forEach((k) => keys.add(k));
      return Array.from(keys);
    }
    if (isVegFamily) {
      ['veg', 'vegetarian', 'lacto vegetarian', 'ovo vegetarian'].forEach((k) => keys.add(k));
      return Array.from(keys);
    }

    const tokenHit = (haystack: string, needle: string): boolean => {
      if (!needle) return false;
      if (haystack === needle) return true;
      // Short needles must be whole tokens, and "veg" must not match "non veg"
      if (needle === 'veg' || needle === 'egg') {
        if (/\bnon[\s-]?veg/.test(haystack)) return false;
        return new RegExp(`(?:^|\\s)${needle}(?:\\s|$)`).test(haystack);
      }
      if (needle.length <= 4) {
        return new RegExp(`(?:^|\\s)${needle.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}(?:\\s|$)`).test(haystack);
      }
      return haystack.includes(needle) || needle.includes(haystack);
    };

    for (const [canonical, aliases] of Object.entries(this.masterAliases)) {
      // Skip diet keys — handled above
      if (canonical === 'veg' || canonical === 'vegetarian' || canonical === 'non veg') continue;
      const group = [canonical, ...(aliases || [])].map((a) => this.normalizeLabel(a)).filter(Boolean);
      const inFamily = group.includes(base) || group.some((g) => tokenHit(base, g) || tokenHit(g, base));
      if (inFamily) group.forEach((g) => keys.add(g));
    }
    // Cereal product family
    if (/\bcereals?\b/.test(base)) {
      keys.add('cereals');
      keys.add('cereals and cereal products');
      keys.add('cereal products');
    }
    // Amber / traffic-light health colors in master are named e.g. "Orange/Brown Color Code"
    if (base === 'amber' || base === 'orange' || base === 'yellow') {
      ['amber', 'orange', 'yellow', 'orange brown color code', 'yellow color code', 'light yellow beige color code', 'orange brown']
        .forEach((k) => keys.add(k));
    }
    if (/\borange\b/.test(base) || /\byellow\b/.test(base)) {
      keys.add('amber');
      keys.add('orange');
      keys.add('yellow');
    }
    return Array.from(keys);
  }

  private labelsMatch(optionName: any, rawValue: any): boolean {
    const opt = this.normalizeLabel(optionName);
    const raw = this.normalizeLabel(rawValue);
    if (!opt || !raw) return false;
    if (opt === raw) return true;

    // Hard block: never equate Veg family with Non Veg
    const optNon = /\bnon[\s-]?veg/.test(opt) || opt === 'nonvegetarian' || opt === 'nonveg';
    const rawNon = /\bnon[\s-]?veg/.test(raw) || raw === 'nonvegetarian' || raw === 'nonveg';
    const optVeg = !optNon && (opt === 'veg' || opt === 'vegetarian' || /\bvegetarian\b/.test(opt));
    const rawVeg = !rawNon && (raw === 'veg' || raw === 'vegetarian' || /\bvegetarian\b/.test(raw));
    if ((optNon && rawVeg) || (rawNon && optVeg)) return false;
    if (optNon && rawNon) return true;
    if (optVeg && rawVeg) return true;

    const rawKeys = new Set(this.expandMatchKeys(rawValue));
    const optKeys = this.expandMatchKeys(optionName);
    if (optKeys.some((k) => rawKeys.has(k))) return true;

    if (raw.length >= 12 && opt.length >= 12 && (opt.includes(raw) || raw.includes(opt))) {
      return true;
    }
    return false;
  }

  /** Prefer exact / Veg↔Vegetarian mapping — never pick Non Veg for Vegetarian. */
  private resolveDietaryPreferenceName(raw: any): string {
    const text = String(raw ?? '').trim();
    if (!text || !this.dietaryPreferenceOptions?.length) return '';

    const lower = this.normalizeLabel(text);
    const exact = this.dietaryPreferenceOptions.find(
      (d) => this.normalizeLabel(d.name) === lower
    );
    if (exact) return exact.name;

    const findBy = (pred: (name: string) => boolean) =>
      this.dietaryPreferenceOptions.find((d) => pred(this.normalizeLabel(d.name) || String(d.name || '').toLowerCase()));

    if (/\bnon[\s-]?veg/.test(lower) || lower === 'nonvegetarian' || lower === 'nonveg') {
      return findBy((n) => /\bnon[\s-]?veg/.test(n) || n === 'nonvegetarian' || n === 'nonveg')?.name || '';
    }
    if (lower === 'vegan' || lower.includes('vegan')) {
      return findBy((n) => n === 'vegan')?.name || '';
    }
    if (lower === 'egg' || lower.includes('egg')) {
      return findBy((n) => n === 'egg' || n === 'eggetarian' || n === 'eggitarian')?.name || '';
    }
    if (lower === 'veg' || lower === 'vegetarian' || lower.includes('vegetarian')) {
      return (
        findBy((n) => n === 'vegetarian')?.name
        || findBy((n) => n === 'veg')?.name
        || ''
      );
    }

    const fuzzy = this.dietaryPreferenceOptions.find((d) => this.labelsMatch(d.name, text));
    return fuzzy?.name || '';
  }

  private resolveHealthColorCodeId(raw: any): number | null {
    const text = String(raw ?? '').trim();
    if (!text || !this.healthColorCodeOptions?.length) return null;
    if (/^\d+$/.test(text)) {
      const byId = this.healthColorCodeOptions.find((h) => String(h.id) === text);
      if (byId) return byId.id;
    }
    const exact = this.healthColorCodeOptions.find(
      (h) => this.normalizeLabel(h.name) === this.normalizeLabel(text)
    );
    if (exact) return exact.id;

    const lower = this.normalizeLabel(text);
    // CSV often uses traffic-light names Amber / Green / Red
    if (lower === 'amber' || lower === 'orange' || lower === 'yellow') {
      const preferred =
        this.healthColorCodeOptions.find((h) => /\borange\b/i.test(h.name))
        || this.healthColorCodeOptions.find((h) => /\byellow\b/i.test(h.name));
      if (preferred) return preferred.id;
    }
    if (lower === 'green') {
      const g = this.healthColorCodeOptions.find((h) => /\bgreen\b/i.test(h.name) && !/\bred\b/i.test(h.name));
      if (g) return g.id;
    }
    if (lower === 'red') {
      const r = this.healthColorCodeOptions.find((h) => /\bred\b/i.test(h.name));
      if (r) return r.id;
    }

    const fuzzy = this.healthColorCodeOptions.find((h) => this.labelsMatch(h.name, text));
    return fuzzy?.id ?? null;
  }

  private findMasterOption(options: any[], rawValue: any): any | null {
    const value = String(rawValue?.id ?? rawValue?.name ?? rawValue ?? '').trim();
    if (!value) return null;
    const list = options || [];
    const byId = list.find((o: any) => String(o.id) === value);
    if (byId) return byId;
    return list.find((o: any) => this.labelsMatch(o.name, value)) || null;
  }

  private resolveClaimObjects(masters: any[], raw: any): any[] {
    const parts = Array.isArray(raw)
      ? raw.map((x) => String(x?.name ?? x?.Name ?? x?.id ?? x ?? '').trim()).filter(Boolean)
      : this.splitCsvParts(raw);
    const seen = new Set<string>();
    const out: any[] = [];
    for (const part of parts) {
      const lower = this.normalizeLabel(part);
      if (!lower || lower === 'none' || lower === 'none declared' || lower === 'na' || lower === 'n a' || lower === 'not applicable') {
        continue;
      }
      const match = this.findMasterOption(masters, part);
      if (!match || match.id == null || match.id === '') continue;
      const key = String(match.id);
      if (seen.has(key)) continue;
      seen.add(key);
      out.push(match);
    }
    return out;
  }

  private collectCsvNutrientTagNames(item: any): string[] {
    const fromGroups: string[] = [];
    (Array.isArray(item?.nutrientGroups) ? item.nutrientGroups : []).forEach((g: any) => {
      (g?.selectedTags || g?.SelectedTags || []).forEach((t: any) => {
        const name = String(t?.tagName ?? t?.TagName ?? t?.name ?? t?.Name ?? t ?? '').trim();
        if (name) fromGroups.push(name);
      });
    });
    const fromColumn = this.splitCsvParts(
      item?.nutrientTags ?? item?.NutrientTags ?? item?.nutrientTagNames ?? ''
    );
    return Array.from(new Set([...fromGroups, ...fromColumn].map((n) => n.trim()).filter(Boolean)));
  }

  private resolveNutrientGroups(raw: any): any[] {
    const parts = Array.isArray(raw)
      ? raw.map((g) => String(g?.name ?? g?.nutrientGroupName ?? g?.id ?? g?.nutrientGroupId ?? g ?? '').trim()).filter(Boolean)
      : this.splitCsvParts(raw);
    const seen = new Set<string>();
    const out: any[] = [];
    for (const part of parts) {
      const match = (this.nutrientGroups || []).find((g: any) =>
        String(g.id) === part ||
        String(g.nutrientGroupId) === part ||
        this.labelsMatch(g.name || g.nutrientGroupName, part)
      );
      if (!match) continue;
      const id = match.id ?? match.nutrientGroupId;
      const key = String(id);
      if (seen.has(key)) continue;
      seen.add(key);
      out.push({
        ...match,
        id,
        name: match.name || match.nutrientGroupName,
        nutrientGroupId: id,
        nutrientGroupName: match.name || match.nutrientGroupName,
        selectedTags: [],
      });
    }
    return out;
  }

  /** Fetch tags for selected groups; prefer CSV tag names when provided, else select all. */
  private async autoSelectNutrientTagsForItem(item: any): Promise<void> {
    if (!item?.nutrientGroups?.length) return;
    await this.onNutrientGroupChange(item);

    const preferred = (item._csvNutrientTagNames || [])
      .map((n: string) => this.normalizeLabel(n))
      .filter(Boolean);
    if (!preferred.length) return;

    const available = this.getItemSelectedTags(item);
    if (!available.length) return;

    const matched = available.filter((t: any) =>
      preferred.includes(this.normalizeLabel(t?.tagName || t?.name))
    );
    // If CSV listed tags and some match, keep only matches; otherwise keep all auto-selected.
    if (matched.length) {
      item.nutrientGroups[0].selectedTags = matched;
    }
  }

  compareNutrientGroup = (a: any, b: any): boolean => {
    const aId = a?.nutrientGroupId ?? a?.id;
    const bId = b?.nutrientGroupId ?? b?.id;
    if (aId != null && bId != null) return String(aId) === String(bId);
    return this.normalizeLabel(a?.name || a?.nutrientGroupName) === this.normalizeLabel(b?.name || b?.nutrientGroupName);
  };

  hasUnresolvedCsv(item: any): boolean {
    const u = item?._csvUnresolved;
    return !!(
      u?.dataSource ||
      u?.foodGroup ||
      u?.foodGroupFAO ||
      u?.season ||
      u?.category ||
      u?.mealType ||
      u?.occasion ||
      u?.mealPreferenceTaxonomy ||
      u?.dietaryPreference ||
      u?.healthColorCode ||
      u?.rawMaterialType
    );
  }

  getUomCount(item: any): number {
    return (item?.ingredientUnitOfMeasurements || []).filter((u: any) => u && !u.isDeleted).length;
  }

  getMicronutrientCount(item: any): number {
    return (item?.nutrientInformation || []).filter(
      (n: any) => n && String(n.nutrientType || '').toLowerCase().includes('micro')
    ).length;
  }

  getUomAmount(uom: any): number {
    return Number(uom?.quantity ?? uom?.valueInGram ?? 0) || 0;
  }

  private resolveMasterIds(options: any[], raw: any): any[] {
    const values: string[] = Array.isArray(raw)
      ? raw.map((v: any) => String(v?.id ?? v?.name ?? v ?? '').trim()).filter(Boolean)
      : String(raw ?? '')
          .split(/[|,;]/)
          .map((s) => s.trim())
          .filter(Boolean);
    const seen = new Set<string>();
    const ids: any[] = [];
    for (const value of values) {
      const matched = this.findMasterOption(options, value);
      if (!matched || matched.id == null || matched.id === '') continue;
      const key = String(matched.id);
      if (seen.has(key)) continue;
      seen.add(key);
      ids.push(matched.id);
    }
    return ids;
  }

  ngAfterViewInit() {
    this.scheduleLucideIconRender();
  }

  private scheduleLucideIconRender(): void {
    if (typeof window !== 'undefined') {
      window.setTimeout(() => {
        if ((window as any).lucide && typeof (window as any).lucide.createIcons === 'function') {
          (window as any).lucide.createIcons();
        }
      }, 50);
    }
  }

  mapMacrosToItem(item: any) {
    item._macros = {
      Calories: 0,
      Carbohydrate: 0,
      Protein: 0,
      Fat: 0,
      Fibre: 0
    };
    if (item.nutrientInformation && Array.isArray(item.nutrientInformation)) {
       item.nutrientInformation.forEach((n: any) => {
         const name = n.nutrient ?? n.Nutrient ?? '';
         const amount = Number(n.amount ?? n.Amount ?? 0) || 0;
         if (item._macros[name] !== undefined) {
            item._macros[name] = amount;
         } else if (/^fiber$/i.test(name) && item._macros.Fibre !== undefined) {
            item._macros.Fibre = amount;
         } else if (/^carbs?$/i.test(name) && item._macros.Carbohydrate !== undefined) {
            item._macros.Carbohydrate = amount;
         }
       });
    }
  }

  syncMacrosToItem(item: any) {
    if (!item.nutrientInformation) item.nutrientInformation = [];
    
    Object.keys(item._macros).forEach(macroName => {
       const val = Number(item._macros[macroName]) || 0;
       const existing = item.nutrientInformation.find((n: any) => n.nutrient === macroName);
       if (existing) {
          existing.amount = val;
       } else {
          item.nutrientInformation.push({
             nutrient: macroName,
             amount: val,
             unit: macroName === 'Calories' ? 'kcal' : 'g',
             nutrientType: 'Macronutrient'
          });
       }
    });
  }

  /**
   * Data source change in bulk preview only stores the selected master id.
   * Never call ingredients/{id}/nutrition-params here — that would N+1 for every row
   * (and dataSource id is not an ingredient id).
   */
  async onDataSourceChange(item: any): Promise<void> {
    const rawDataSource = String(item?.dataSource || '').trim();
    if (!rawDataSource || rawDataSource === '0' || rawDataSource === 'null' || rawDataSource === 'undefined') {
      return;
    }
    const dsOption = this.dataSourceOptions.find(
      (d) =>
        String(d.name).trim().toLowerCase() === rawDataSource.toLowerCase() ||
        String(d.id) === rawDataSource
    );
    if (dsOption?.id != null) {
      item.dataSource = dsOption.id;
    }
  }

  private extractNutrientTagsFromResponse(res: any): Array<{ tagId: any; tagName: string }> {
    let tagsList: any[] = [];
    if (res?.response?.nutrientTagList) tagsList = res.response.nutrientTagList;
    else if (res?.response?.nutrientTags) tagsList = res.response.nutrientTags;
    else if (Array.isArray(res?.response?.items)) tagsList = res.response.items;
    else if (Array.isArray(res?.data?.items)) tagsList = res.data.items;
    else if (Array.isArray(res?.data?.nutrientTagList)) tagsList = res.data.nutrientTagList;
    else if (Array.isArray(res?.response)) tagsList = res.response;
    else if (Array.isArray(res?.data)) tagsList = res.data;
    else if (Array.isArray(res)) tagsList = res;

    const out: Array<{ tagId: any; tagName: string }> = [];
    if (!Array.isArray(tagsList)) return out;
    for (const t of tagsList) {
      const tagId = t.id || t.tagId || t.nutrientTagId || t.nutrientTagID;
      const tagName = t.name || t.tagName || t.nutrientTagName;
      if (tagId == null || !tagName) continue;
      out.push({ tagId, tagName });
    }
    return out;
  }

  private async getNutrientTagsForGroupCached(groupId: string): Promise<Array<{ tagId: any; tagName: string }>> {
    const key = String(groupId);
    if (this.nutrientTagsByGroupId.has(key)) {
      return this.nutrientTagsByGroupId.get(key)!;
    }
    if (this.nutrientTagsByGroupIdInflight.has(key)) {
      return this.nutrientTagsByGroupIdInflight.get(key)!;
    }
    const promise = (async () => {
      try {
        const res: any = await this.masterService.getNutrientTagsByGroupId(key);
        const tags = this.extractNutrientTagsFromResponse(res);
        this.nutrientTagsByGroupId.set(key, tags);
        return tags;
      } catch (error) {
        console.error('Error fetching nutrient tags for group', key, error);
        this.nutrientTagsByGroupId.set(key, []);
        return [] as Array<{ tagId: any; tagName: string }>;
      } finally {
        this.nutrientTagsByGroupIdInflight.delete(key);
      }
    })();
    this.nutrientTagsByGroupIdInflight.set(key, promise);
    return promise;
  }

  async onNutrientGroupChange(item: any) {
    if (!item.nutrientGroups) return;

    if (item.nutrientGroups.length === 0) {
      return;
    }

    const selectedGroupIds = item.nutrientGroups
      .map((g: any) => g.nutrientGroupId || g.id)
      .filter((id: any) => id != null && id !== '')
      .map((id: any) => String(id));
    if (!selectedGroupIds.length) return;

    item.nutrientGroups.forEach((g: any) => {
      if (g && !Array.isArray(g.selectedTags)) g.selectedTags = [];
    });

    try {
      const tagLists = await Promise.all(
        selectedGroupIds.map((groupId: string) => this.getNutrientTagsForGroupCached(groupId))
      );

      let allFetchedTags: Array<{ tagId: any; tagName: string }> = [];
      tagLists.forEach((tags) => {
        allFetchedTags = allFetchedTags.concat(tags || []);
      });

      if (allFetchedTags.length > 0) {
        const uniqueTags = Array.from(new Map(allFetchedTags.map((t) => [String(t.tagId), t])).values());
        item.nutrientGroups.forEach((g: any) => {
          if (g) g.selectedTags = [...uniqueTags];
        });

        const existingTagIds = new Set(this.nutrientTags.map((t) => String(t.tagId)));
        uniqueTags.forEach((newTag) => {
          if (!existingTagIds.has(String(newTag.tagId))) {
            this.nutrientTags = [...this.nutrientTags, { tagId: newTag.tagId, tagName: newTag.tagName }];
            existingTagIds.add(String(newTag.tagId));
          }
        });
      } else {
        item.nutrientGroups.forEach((g: any) => {
          if (g) g.selectedTags = [];
        });
      }
    } catch (error) {
      console.error('Error fetching nutrient tags for auto-select', error);
    }
    this.scheduleLucideIconRender();
  }


  viewUoms(item: any) {
    this.selectedUomItem = item;
    if (!item.ingredientUnitOfMeasurements) {
       item.ingredientUnitOfMeasurements = [];
    }
    this.modalService.open(this.uomModal, { size: 'xl', centered: true, scrollable: true }).result.then(
      () => this.scheduleLucideIconRender(),
      () => this.scheduleLucideIconRender()
    );
    this.scheduleLucideIconRender();
  }

  addUomRow() {
    if (!this.selectedUomItem.ingredientUnitOfMeasurements) {
       this.selectedUomItem.ingredientUnitOfMeasurements = [];
    }
    this.selectedUomItem.ingredientUnitOfMeasurements.push({
       size: null,
       type: null,
       valueInGram: 0,
       quantity: 0,
       unit: null,
       servingUnitMasterId: null,
       isPreferred: false,
       isActive: true,
       isDeleted: false
    });
    this.scheduleLucideIconRender();
  }

  removeUomRow(index: number) {
    this.selectedUomItem.ingredientUnitOfMeasurements.splice(index, 1);
    this.scheduleLucideIconRender();
  }

  get previewTotalPages(): number {
    const total = this.parsedIngredients?.length || 0;
    return Math.max(1, Math.ceil(total / this.previewPageSize));
  }

  get previewPageStart(): number {
    return (this.previewPage - 1) * this.previewPageSize;
  }

  get pagedIngredients(): any[] {
    const start = this.previewPageStart;
    return (this.parsedIngredients || []).slice(start, start + this.previewPageSize);
  }

  trackByIngredient(_index: number, item: any): any {
    return item?.id ?? item?.Id ?? item?.name ?? _index;
  }

  prevPreviewPage(): void {
    if (this.previewPage > 1) {
      this.previewPage -= 1;
      this.scheduleLucideIconRender();
    }
  }

  nextPreviewPage(): void {
    if (this.previewPage < this.previewTotalPages) {
      this.previewPage += 1;
      this.scheduleLucideIconRender();
    }
  }

  applyBulkStatus(status?: string): void {
    const next = String(status ?? this.bulkStatus ?? 'Draft').trim();
    const normalized = next.toLowerCase() === 'draft' ? 'Draft' : 'Active';
    this.bulkStatus = normalized;
    for (const item of this.parsedIngredients || []) {
      if (item) item.status = normalized;
    }
    this.scheduleLucideIconRender();
  }

  deleteRow(index: number) {
    this.parsedIngredients.splice(index, 1);
    if (this.previewPage > this.previewTotalPages) {
      this.previewPage = this.previewTotalPages;
    }
    this.scheduleLucideIconRender();
  }

  onSizeChange(uom: any): void {
    // Manual fill only — do not auto-populate measure / amount / unit from serving size.
    if (!uom) return;
    uom.servingUnitMasterId = this.resolveServingUnitMasterId(uom);
  }

  onUomMeasureChange(uom: any): void {
    if (!uom) return;
    uom.servingUnitMasterId = this.resolveServingUnitMasterId(uom);
  }

  onUomUnitChange(uom: any): void {
    if (!uom) return;
    uom.type = this.normalizeUnitValue(uom?.type);
    uom.servingUnitMasterId = this.resolveServingUnitMasterId(uom);
  }

  onUomAmountChange(uom: any): void {
    const amt = Number(uom?.quantity ?? uom?.valueInGram) || 0;
    uom.quantity = amt;
    uom.valueInGram = amt;
  }

  private resolveServingUnitMasterId(uom: any): any {
    if (!uom || !this.masterServingUnits?.length) return uom?.servingUnitMasterId ?? null;
    const size = String(uom.size ?? '').trim().toLowerCase();
    const measure = String(uom.unit ?? '').trim().toLowerCase();
    const unit = String(uom.type ?? '').trim().toLowerCase();
    const match = this.masterServingUnits.find((m: any) => {
      const mSize = String(m.size ?? '').trim().toLowerCase();
      const mMeasure = String(m.unit ?? m.name ?? '').trim().toLowerCase();
      const mType = String(m.type ?? '').trim().toLowerCase();
      const sizeOk = !size || mSize === size;
      const measureOk = !measure || mMeasure === measure;
      const typeOk = !unit || mType === unit || mType.startsWith(unit);
      return sizeOk && measureOk && typeOk && (size || measure);
    });
    return match?.id ?? uom.servingUnitMasterId ?? null;
  }

  private normalizeUnitValue(raw: any): string {
    const text = String(raw ?? '').trim();
    if (!text) return '';
    const exact = (this.unitOptions || []).find((o) => String(o.value).toLowerCase() === text.toLowerCase());
    if (exact) return exact.value;
    const aliases: Record<string, string> = {
      g: 'g', gm: 'g', gram: 'g', grams: 'g',
      ml: 'ml', millilitre: 'ml', milliliter: 'ml',
      kg: 'Kg', kilogram: 'Kg',
      l: 'Litre', liter: 'Litre', litre: 'Litre',
    };
    return aliases[text.toLowerCase()] || text;
  }

  private normalizeMeasureValue(raw: any): string {
    const text = String(raw ?? '').trim();
    if (!text) return '';
    const exact = (this.servingMeasureOptions || []).find(
      (o) => String(o.value).toLowerCase() === text.toLowerCase()
    );
    if (exact) return exact.value;
    const aliases: Record<string, string> = {
      weight: 'Grams',
      wt: 'Grams',
      mass: 'Grams',
      gram: 'Grams',
      grams: 'Grams',
      g: 'Grams',
      volume: 'Ml',
      vol: 'Ml',
      ml: 'Ml',
      cup: 'Cup',
      tbsp: 'Table Spoon',
      tsp: 'Tea Spoon',
      'table spoon': 'Table Spoon',
      tablespoon: 'Table Spoon',
      'tea spoon': 'Tea Spoon',
      teaspoon: 'Tea Spoon',
      piece: 'Piece',
      slice: 'Slice',
      bowl: 'Bowl',
      glass: 'Glass',
    };
    return aliases[text.toLowerCase()] || text;
  }

  private ensureOptionPresent(options: Array<{ name: string; value: string }>, raw: any): void {
    const value = String(raw ?? '').trim();
    if (!value || !options) return;
    const exists = options.some((o) => String(o.value).toLowerCase() === value.toLowerCase());
    if (!exists) options.push({ name: value, value });
  }

  calculateUomMacro(macroName: string, uomAmount: any): string {
    const baseMacro = Number(this.selectedUomItem?._macros?.[macroName]) || 0;
    const amount = Number(uomAmount) || 0;
    return ((baseMacro * amount) / 100).toFixed(2);
  }

  saveMicronutrientsAndClose(modal: any) {
    // Micronutrients are two-way bound to selectedMicronutrientsList
    // So we just close
    modal.close();
    this.scheduleLucideIconRender();
  }

  private extractList(response: any): any[] {
    if (!response) return [];
    const root = response.response ?? response.data ?? response.result ?? response;
    
    // Quick checks
    if (Array.isArray(root)) return root;
    if (Array.isArray(root?.servingunitList)) return root.servingunitList;
    if (Array.isArray(root?.servingUnitList)) return root.servingUnitList;
    if (Array.isArray(root?.servingUnits)) return root.servingUnits;
    if (Array.isArray(root?.items)) return root.items;
    if (Array.isArray(root?.records)) return root.records;
    if (Array.isArray(root?.list)) return root.list;
    if (Array.isArray(root?.data)) return root.data;

    // Deep search
    for (const value of Object.values(root || {})) {
      if (Array.isArray(value)) return value;
    }
    return [];
  }

  async loadMasterData() {
    try {
      const metadataResponse: any = await this.masterService.getIngredientMetadata(true);
      const metadata = metadataResponse?.response ?? metadataResponse?.data ?? metadataResponse?.result ?? metadataResponse ?? {};
      
      const normalizeList = (arr: any[], idKeys: string[], nameKeys: string[]) => arr.map((item: any) => ({
        ...item,
        id: item.id ?? idKeys.reduce((v: any, k: string) => v ?? item[k], undefined as any),
        name: item.name ?? nameKeys.reduce((v: any, k: string) => v ?? item[k], undefined as any)
      }));

      this.dataSourceOptions = normalizeList(
        this.getMetadataSectionList(metadata, ['ingredientTypes', 'IngredientTypes', 'dataSources', 'DataSources']),
        ['ingredientTypeId', 'dataSourceId', 'sourceId'],
        ['ingredientTypeName', 'dataSourceName', 'sourceName']
      );
      this.foodGroups = normalizeList(
        this.getMetadataSectionList(metadata, ['foodGroups', 'FoodGroups', 'foodGroupList', 'FoodGroupList']),
        ['foodGroupId', 'groupId'],
        ['foodGroupName', 'groupName']
      );
      this.foodGroupsFAO = normalizeList(
        this.getMetadataSectionList(metadata, ['foodExchanges', 'FoodExchanges', 'foodExchangeList', 'FoodExchangeList']),
        ['foodExchangeId', 'exchangeId', 'foodGroupFAOId'],
        ['foodExchangeName', 'exchangeName']
      );
      this.seasons = normalizeList(
        this.getMetadataSectionList(metadata, ['seasons', 'Seasons', 'seasonList', 'SeasonList']),
        ['seasonId', 'SeasonId'],
        ['seasonName']
      );
      this.nutrientGroups = this.getMetadataSectionList(metadata, ['nutrientGroups', 'NutrientGroups', 'nutrientGroupList', 'NutrientGroupList', 'metadataList', 'MetadataList']).map((g: any) => ({
        ...g,
        id: g.id || g.nutrientGroupId || g.groupId || g.nutrientGroupID,
        name: g.name || g.nutrientGroupName || g.groupName
      }));
      this.nutrientTags = this.getMetadataSectionList(metadata, ['nutrientTags', 'NutrientTags']).map((t: any) => ({
        ...t,
        tagId: t.id || t.tagId,
        tagName: t.name || t.tagName
      }));

      const normalizeClaimOptions = (arr: any[], typeKey: string, typeValue: number) =>
        normalizeList(arr, ['id'], ['name']).map((item: any) => ({
          ...item,
          id: item.id != null ? String(item.id) : item.id,
          [typeKey]: typeValue
        }));

      this.allergensContains = normalizeClaimOptions(
        this.getMetadataSectionList(metadata, ['ingredientAllergens', 'IngredientAllergens', 'allergenContains', 'AllergenContains']),
        'allergenType',
        1
      );
      this.allergensFree = normalizeClaimOptions(
        this.getMetadataSectionList(metadata, ['allergenFree', 'AllergenFree', 'freeAllergens', 'FreeAllergens']),
        'allergenType',
        2
      );
      this.intolerancesContains = normalizeClaimOptions(
        this.getMetadataSectionList(metadata, ['intolerances', 'Intolerances', 'intoleranceContains', 'IntoleranceContains']),
        'intoleranceType',
        1
      );
      this.intolerancesFree = normalizeClaimOptions(
        this.getMetadataSectionList(metadata, ['intoleranceFree', 'IntoleranceFree', 'freeIntolerances', 'FreeIntolerances']),
        'intoleranceType',
        2
      );

      this.categories = normalizeList(
        this.getMetadataSectionList(metadata, ['categories', 'Categories', 'categoryList', 'CategoryList']),
        ['categoryId', 'itemCategoryId'],
        ['categoryName', 'itemCategoryName']
      );
      this.mealTypes = normalizeList(
        this.getMetadataSectionList(metadata, ['mealTypes', 'MealTypes', 'mealTypeList', 'MealTypeList']),
        ['mealTypeId'],
        ['mealTypeName']
      );
      this.occasions = normalizeList(
        this.getMetadataSectionList(metadata, ['occasions', 'Occasions', 'occasionList', 'OccasionList']),
        ['occasionId'],
        ['occasionName']
      );
      this.mealPreferences = normalizeList(
        this.getMetadataSectionList(metadata, ['mealPreferences', 'MealPreferences', 'mealPreferenceList', 'MealPreferenceList']),
        ['mealPreferenceId'],
        ['mealPreferenceName']
      );

      this.rawMaterialTypeOptions = normalizeList(
        this.getMetadataSectionList(metadata, ['rawMaterialTypes', 'RawMaterialTypes', 'productTypes', 'ProductTypes']),
        ['rawTypeId', 'productTypeId', 'typeId'],
        ['rawTypeName', 'productTypeName', 'typeName']
      );

      this.glycemicIndexOptions = this.getMetadataSectionList(metadata, ['glycemicIndexes', 'GlycemicIndexes'])
        .map((item: any) => {
          const name = String(item?.name ?? item?.Name ?? '').trim();
          if (!name) return null;
          const code = Number(item?.code ?? item?.Code ?? item?.sortOrder ?? item?.SortOrder ?? 0);
          const id = String(item?.id ?? item?.Id ?? (Number.isFinite(code) && code > 0 ? code : name));
          return { id, name, code: Number.isFinite(code) && code > 0 ? code : 0 };
        })
        .filter((x: any) => !!x)
        .sort((a: any, b: any) => (a.code || 0) - (b.code || 0) || a.name.localeCompare(b.name));
      if (!this.glycemicIndexOptions.length) {
        this.glycemicIndexOptions = [
          { id: '1', name: 'Low', code: 1 },
          { id: '2', name: 'Medium', code: 2 },
          { id: '3', name: 'High', code: 3 },
        ];
      }

      const hccRaw = metadata.healthColorCodes || metadata.HealthColorCodes || [];
      if (Array.isArray(hccRaw) && hccRaw.length) {
        this.healthColorCodeOptions = hccRaw
          .map((item: any) => {
            const id = Number(item?.id ?? item?.Id ?? 0);
            const name = String(
              item?.label ?? item?.Label ?? item?.name ?? item?.Name ?? item?.title ?? item?.Title ?? ''
            ).trim();
            return Number.isFinite(id) && id > 0 && name ? { id, name } : null;
          })
          .filter((h: { id: number; name: string } | null): h is { id: number; name: string } => !!h);
      }

      try {
        const recipeMetaRes: any = await this.masterService.getRecipeMetadata('dietaryPreferences');
        const recipeMeta = recipeMetaRes?.response ?? recipeMetaRes?.data ?? recipeMetaRes;
        const dietList = recipeMeta?.dietaryPreferences || recipeMeta?.DietaryPreferences || [];
        if (Array.isArray(dietList) && dietList.length) {
          this.dietaryPreferenceOptions = dietList
            .map((d: any) => ({
              id: String(d?.id ?? d?.Id ?? ''),
              name: String(d?.name ?? d?.Name ?? '').trim(),
            }))
            .filter((d: { id: string; name: string }) => !!d.id && !!d.name);
        }
      } catch {
        // optional
      }

      if (!this.healthColorCodeOptions.length) {
        try {
          const hccRes: any = await this.masterService.getHealthColorCodes({ pageNo: 1, pageSize: 100 } as any);
          const root = hccRes?.response ?? hccRes?.data ?? hccRes;
          const items = Array.isArray(root?.items) ? root.items : Array.isArray(root) ? root : [];
          this.healthColorCodeOptions = items
            .map((item: any) => {
              const id = Number(item?.id ?? item?.Id ?? 0);
              const name = String(
                item?.label ?? item?.Label ?? item?.name ?? item?.Name ?? item?.title ?? item?.Title ?? ''
              ).trim();
              return Number.isFinite(id) && id > 0 && name ? { id, name } : null;
            })
            .filter((h: { id: number; name: string } | null): h is { id: number; name: string } => !!h);
        } catch {
          // optional
        }
      }

      // Seed UOM dropdown defaults immediately — never block preview open on servingunit/list
      // (that master list can be huge / slow and was leaving the UI stuck on "Parsing file…").
      this.ensureDefaultUomOptions();
      void this.loadServingUnitsInBackground();
    } catch (error) {
      console.error('Failed to load metadata', error);
      this.ensureDefaultUomOptions();
    }
  }

  /** Static Size / Measure / Unit options so preview works without waiting for servingunit/list. */
  private ensureDefaultUomOptions(): void {
    const sizeMap = new Map<string, { name: string; value: string }>();
    const measureMap = new Map<string, { name: string; value: string }>();
    const unitMap = new Map<string, { name: string; value: string }>();
    const addOpt = (map: Map<string, { name: string; value: string }>, raw: any) => {
      const value = String(raw ?? '').trim();
      if (!value) return;
      const key = value.toLowerCase();
      if (!map.has(key)) map.set(key, { name: value, value });
    };

    (this.servingSizeOptions || []).forEach((o) => addOpt(sizeMap, o.value ?? o.name));
    (this.servingMeasureOptions || []).forEach((o) => addOpt(measureMap, o.value ?? o.name));
    (this.unitOptions || []).forEach((o) => addOpt(unitMap, o.value ?? o.name));

    ['Standard', '1', '5', '10', '25', '50', '100', 'Small', 'Medium', 'Large', '1/4', '1/2', '1/3', '3/4', '2', '3']
      .forEach((s) => addOpt(sizeMap, s));
    ['Grams', 'Standard Cup', 'Cup', 'Table Spoon', 'Tea Spoon', 'Piece', 'Slice', 'Bowl', 'Glass', 'weight', 'Weight']
      .forEach((m) => addOpt(measureMap, m));
    ['Gram', 'Ml', 'Kg', 'Litre', 'Ounce', 'Pound', 'Cup', 'Tablespoon', 'Teaspoon', 'Piece', 'Slice', 'g', 'ml']
      .forEach((u) => addOpt(unitMap, u));

    (this.masterServingUnits || []).forEach((u: any) => {
      addOpt(sizeMap, u.size);
      addOpt(measureMap, u.unit || u.name);
      addOpt(unitMap, this.normalizeUnitValue(u.type));
    });

    this.servingSizeOptions = Array.from(sizeMap.values());
    this.servingMeasureOptions = Array.from(measureMap.values());
    this.unitOptions = Array.from(unitMap.values()).sort((a, b) => a.name.localeCompare(b.name));
  }

  /** Optional enrichment — must not block BulkUpload → Preview transition. */
  private async loadServingUnitsInBackground(): Promise<void> {
    try {
      const req = { pageSize: 200, pageNo: 1, searchString: '', sortBy: '', sortOrder: '' };
      const timeoutMs = 8000;
      const uomResponse: any = await Promise.race([
        this.masterService.getServingUnits(req as any),
        new Promise((_, reject) => setTimeout(() => reject(new Error('servingunit/list timeout')), timeoutMs)),
      ]);
      const uomList = this.extractList(uomResponse);
      this.masterServingUnits = uomList || [];
      this.ensureDefaultUomOptions();

      // Re-resolve CSV UOM → servingUnitMasterId now that masters are available
      for (const item of this.parsedIngredients || []) {
        if (!Array.isArray(item.ingredientUnitOfMeasurements)) continue;
        for (const uom of item.ingredientUnitOfMeasurements) {
          if (!uom) continue;
          uom.size = String(uom.size ?? '').trim() || uom.size;
          uom.unit = this.normalizeMeasureValue(uom.unit);
          uom.type = this.normalizeUnitValue(uom.type) || uom.type;
          this.ensureOptionPresent(this.servingSizeOptions, uom.size);
          this.ensureOptionPresent(this.servingMeasureOptions, uom.unit);
          this.ensureOptionPresent(this.unitOptions, uom.type);
          uom.servingUnitMasterId = this.resolveServingUnitMasterId(uom);
        }
      }
    } catch (uomError) {
      console.warn('Serving units list skipped/failed (preview continues with defaults):', uomError);
    }
  }

  private getMetadataSectionList(metadata: any, keys: string[]): any[] {
    for (const key of keys) {
      if (metadata[key] && Array.isArray(metadata[key])) {
        return metadata[key];
      }
    }
    return [];
  }

  async save(): Promise<void> {
    // Upload any pending image files first (prefer URL over huge base64)
    for (const item of this.parsedIngredients) {
      if (item.imageFile instanceof File) {
        try {
          const uploadedUrl = await this.masterService.uploadMedia(item.imageFile, 'ingredient');
          if (uploadedUrl) {
            item.imagePath = uploadedUrl;
            item.imagePreview = uploadedUrl;
          }
        } catch (err) {
          console.error('Failed to upload ingredient image', err);
        }
      }
    }

    // Map properties to match IngredientCreateDto format
    const payload = this.parsedIngredients.map(item => {
      this.syncMacrosToItem(item);

      const toArray = (val: any) => {
        if (Array.isArray(val)) return val.map((s: any) => String(s).trim()).filter((s: string) => s);
        if (typeof val === 'string') return val.split(',').map(s => s.trim()).filter(s => s);
        return [];
      };

      const toNullableNumber = (val: any): number | null => {
        if (val === null || val === undefined || val === '') return null;
        const n = Number(val);
        return Number.isFinite(n) && n > 0 ? n : null;
      };

      const mapClaimItems = (items: any[] | undefined) =>
        (Array.isArray(items) ? items : [])
          .map((x: any) => ({
            id: Number(x?.id ?? x),
            name: x?.name || '',
            allergenType: x?.allergenType,
            intoleranceType: x?.intoleranceType
          }))
          .filter((x: any) => Number.isFinite(x.id) && x.id > 0);

      const resolveFoodGroupFAOId = (): number | null => {
        if (item.foodGroupFAOId != null && item.foodGroupFAOId !== '') {
          return toNullableNumber(item.foodGroupFAOId);
        }
        if (Array.isArray(item.foodGroupFAOIds) && item.foodGroupFAOIds.length > 0) {
          const first = item.foodGroupFAOIds[0];
          const asNum = toNullableNumber(first);
          if (asNum) return asNum;
          const matched = this.foodGroupsFAO.find((g: any) =>
            String(g.id) === String(first) ||
            String(g.name || '').toLowerCase() === String(first).toLowerCase()
          );
          return matched ? toNullableNumber(matched.id) : null;
        }
        return null;
      };

      const dataSourceId = toNullableNumber(item.dataSource);
      const commonNames = toArray(item.commonNames?.length ? item.commonNames : item.commonName);
      const aliases = toArray(item.aliases);
      const regionalNames = toArray(item.regionalNames);

      const nutrientGroups = Array.isArray(item.nutrientGroups)
        ? item.nutrientGroups.map((g: any) => ({
            nutrientGroupId: String(g.id || g.nutrientGroupId || ''),
            nutrientGroupName: g.name || g.nutrientGroupName || '',
            selectedTags: (g.selectedTags || []).map((t: any) => ({
              tagId: Number(t.tagId || t.id),
              tagName: t.tagName || t.name || ''
            })).filter((t: any) => Number.isFinite(t.tagId) && t.tagId > 0)
          }))
        : [];

      const nutrientInformation = (item.nutrientInformation || []).map((n: any) => ({
        nutrientParamId: toNullableNumber(n.nutrientParamId || n.nutritionId),
        nutrient: n.nutrient || n.nutrition || '',
        nutrientType: n.nutrientType || n.groupName || '',
        unit: n.unit || n.measurement || '',
        amount: Number(n.amount ?? n.value) || 0
      }));

      const ingredientUnitOfMeasurements = (item.ingredientUnitOfMeasurements || [])
        .filter((u: any) => u && !u.isDeleted && (
          u.servingUnitMasterId || u.unit || u.size || u.type
          || Number(u.quantity ?? u.valueInGram) > 0
        ))
        .map((u: any) => {
          const amount = Number(u.quantity ?? u.valueInGram) || 0;
          return {
            servingUnitMasterId: toNullableNumber(u.servingUnitMasterId) ?? toNullableNumber(this.resolveServingUnitMasterId(u)),
            unit: u.unit || '',
            size: u.size || null,
            type: u.type || null,
            valueInGram: amount,
            isActive: u.isActive !== false,
            isDeleted: !!u.isDeleted,
            isPreferred: !!u.isPreferred,
            quantity: amount
          };
        });

      // Ensure at least one preferred when multiple UOMs present
      if (ingredientUnitOfMeasurements.length && !ingredientUnitOfMeasurements.some((u: any) => u.isPreferred)) {
        ingredientUnitOfMeasurements[0].isPreferred = true;
      }

      const primaryUom = [...ingredientUnitOfMeasurements]
        .sort((a, b) => Number(b.isPreferred) - Number(a.isPreferred))
        [0];
      const uomText = primaryUom
        ? `${primaryUom.size ? primaryUom.size + ' ' : ''}${primaryUom.unit || primaryUom.type || ''}`.trim()
        : null;
      const imagePath = item.imagePath || item.imageUrl ||
        (typeof item.imagePreview === 'string' && !item.imagePreview.startsWith('data:') ? item.imagePreview : null) ||
        item.imagePreview ||
        null;

      const toIdList = (ids: any[]): number[] =>
        (Array.isArray(ids) ? ids : [])
          .map((id) => toNullableNumber(id))
          .filter((id): id is number => id != null);

      const categoryIds = toIdList(
        Array.isArray(item.categoryIds) && item.categoryIds.length
          ? item.categoryIds
          : item.itemCategoryId != null
            ? [item.itemCategoryId]
            : []
      );
      const mealTypeIds = toIdList(item.mealTypeIds);
      const occasionIds = toIdList(item.occasionIds);
      const mealPreferenceIds = toIdList(item.mealPreferenceIds);
      const foodGroupIds = toIdList(
        Array.isArray(item.foodGroupIds) && item.foodGroupIds.length
          ? item.foodGroupIds
          : item.foodGroupId != null
            ? [item.foodGroupId]
            : []
      );
      const foodGroupFAOIds = toIdList(
        Array.isArray(item.foodGroupFAOIds) && item.foodGroupFAOIds.length
          ? item.foodGroupFAOIds
          : item.foodGroupFAOId != null
            ? [item.foodGroupFAOId]
            : []
      );

      const dietaryName = String(item.mealPreference || '').trim();
      const lower = dietaryName.toLowerCase();
      let isVeg: boolean | null = null;
      if (/non[-\s]?veg/i.test(dietaryName) || lower === 'egg' || lower === 'eggetarian') isVeg = false;
      else if (lower === 'veg' || lower === 'vegetarian' || lower === 'vegan') isVeg = true;

      const rowIndex = this.parsedIngredients.indexOf(item) + 1;
      const resolvedName = String(item.name || item.ingredientName || '').trim()
        || `Untitled Ingredient (Row ${rowIndex || 1})`;

      return {
        ingredientName: resolvedName,
        scientificName: item.scientificName || '',
        brandName: item.brandName || null,
        allowDuplicate: true,
        description: item.description || null,
        commonName: commonNames[0] || '',
        aliases,
        commonNames,
        regionalNames,
        glycemicIndex: this.mapGlycemicIndexCategoryToApi(item.glycemicIndex),
        insNumber: item.insNumber || null,
        dataSource: (() => {
          const ds = this.dataSourceOptions.find((d: any) => String(d.id) === String(item.dataSource));
          return ds?.name || (item.dataSource != null ? String(item.dataSource) : null);
        })(),
        itemTypeId: dataSourceId,
        itemType: 'Raw Ingredient',
        mealPreference: dietaryName || null,
        isVeg,
        healthColorCodeId: toNullableNumber(item.healthColorCodeId),
        rawTypeId: toNullableNumber(item.rawTypeId),
        foodGroupId: foodGroupIds[0] ?? toNullableNumber(item.foodGroupId),
        foodGroupIds,
        foodGroupFAOId: foodGroupFAOIds[0] ?? resolveFoodGroupFAOId(),
        foodGroupFAOIds,
        seasonId: toNullableNumber(this.expandAllSeasons(item.seasonIds || [item.seasonId])[0]),
        seasonIds: this.expandAllSeasons(item.seasonIds || [item.seasonId])
          .map(id => toNullableNumber(id))
          .filter(id => id !== null),
        categoryIds,
        itemCategoryId: categoryIds[0] ?? toNullableNumber(item.itemCategoryId),
        category: item.category
          || this.categories.find((c: any) => String(c.id) === String(item.itemCategoryId))?.name
          || null,
        mealTypeIds,
        mealType: mealTypeIds[0] ?? toNullableNumber(item.mealType),
        occasionIds,
        mealPreferenceIds,
        status: item.status || 'Draft',
        imagePath,
        uom: uomText,
        quantity: primaryUom ? (Number(primaryUom.quantity) || Number(primaryUom.valueInGram) || 0) : 0,
        servingSize: primaryUom ? (Number(primaryUom.quantity) || Number(primaryUom.valueInGram) || 0) : null,
        calories: Number(item._macros?.Calories ?? item.calories ?? 0) || 0,
        carbs: Number(item._macros?.Carbohydrate ?? item.carbs ?? 0) || 0,
        protein: Number(item._macros?.Protein ?? item.protein ?? 0) || 0,
        fat: Number(item._macros?.Fat ?? item.fat ?? 0) || 0,
        fiber: Number(item._macros?.Fibre ?? item.fiber ?? 0) || 0,
        allergens: {
          contains: mapClaimItems(item.allergens?.contains),
          free: mapClaimItems(item.allergens?.free)
        },
        intolerances: {
          contains: mapClaimItems(item.intolerances?.contains),
          free: mapClaimItems(item.intolerances?.free)
        },
        nutrientGroups,
        nutrientInformation,
        ingredientUnitOfMeasurements,
        knownAllergen: (item.allergens?.contains?.length || 0) > 0,
        rareAllergen: (item.allergens?.free?.length || 0) > 0,
        localName: regionalNames[0] || null,
        commonlyUsed: commonNames.length > 0 ? 'Yes' : 'No',
        stateOfProduct: (() => {
          const s = String(item.stateOfProduct || 'Solid').trim();
          if (/^liquid|wet|fluid$/i.test(s)) return 'Liquid';
          return 'Solid';
        })(),
      };
    });

    this.activeModal.close(payload);
  }

  onImageUpload(event: any, item: any) {
    const file = event.target.files[0];
    if (file) {
      item.imageFile = file;
      const reader = new FileReader();
      reader.onload = (e: any) => {
        item.imagePreview = e.target.result;
        this.scheduleLucideIconRender();
      };
      reader.readAsDataURL(file);
    }
  }

  viewMicronutrients(item: any) {
    this.selectedMicronutrientsItem = item;
    if (!item.nutrientInformation) {
       item.nutrientInformation = [];
    }
    this.selectedMicronutrientsList = item.nutrientInformation.filter((n: any) =>
      String(n.nutrientType || '').toLowerCase().includes('micro')
    );
    
    this.modalService.open(this.micronutrientsModal, { size: 'xl', centered: true, scrollable: true }).result.then(
      () => this.scheduleLucideIconRender(),
      () => this.scheduleLucideIconRender()
    );
    this.scheduleLucideIconRender();
  }

  onTagsChange(item: any, selectedTags: any[]) {
     if (item.nutrientGroups && item.nutrientGroups.length > 0) {
        item.nutrientGroups[0].selectedTags = selectedTags;
     }
  }

  getNutrientTagCount(item: any): number {
    return this.getItemSelectedTags(item).length;
  }

  getItemSelectedTags(item: any): any[] {
    if (!item?.nutrientGroups?.length) return [];
    const tags = item.nutrientGroups[0]?.selectedTags;
    return Array.isArray(tags) ? tags : [];
  }

  getSelectedTagsForModal(): any[] {
    return this.getItemSelectedTags(this.selectedNutrientTagsItem);
  }

  async viewNutrientTags(item: any): Promise<void> {
    if (!item?.nutrientGroups?.length) {
      return;
    }

    this.selectedNutrientTagsItem = item;
    if (!item.nutrientGroups[0].selectedTags) {
      item.nutrientGroups[0].selectedTags = [];
    }

    this.nutrientTagToAdd = null;
    await this.loadNutrientTagOptionsForItem(item);

    this.modalService.open(this.nutrientTagsModal, { size: 'md', centered: true, scrollable: true }).result.then(
      () => this.scheduleLucideIconRender(),
      () => this.scheduleLucideIconRender()
    );
    this.scheduleLucideIconRender();
  }

  removeNutrientTag(tag: any): void {
    const item = this.selectedNutrientTagsItem;
    if (!item?.nutrientGroups?.[0]?.selectedTags) return;

    const tagId = tag?.tagId ?? tag?.id;
    item.nutrientGroups[0].selectedTags = item.nutrientGroups[0].selectedTags.filter((t: any) => {
      const currentId = t?.tagId ?? t?.id;
      if (tagId != null && currentId != null) {
        return String(currentId) !== String(tagId);
      }
      return (t?.tagName || t?.name) !== (tag?.tagName || tag?.name);
    });

    this.refreshNutrientTagOptionsForItem(item);
    this.scheduleLucideIconRender();
  }

  onAddNutrientTag(tag: any): void {
    const item = this.selectedNutrientTagsItem;
    if (!item?.nutrientGroups?.[0] || !tag) return;

    if (!Array.isArray(item.nutrientGroups[0].selectedTags)) {
      item.nutrientGroups[0].selectedTags = [];
    }

    const normalizedTag = {
      tagId: tag.tagId ?? tag.id,
      tagName: tag.tagName ?? tag.name,
    };

    const exists = item.nutrientGroups[0].selectedTags.some((t: any) =>
      String(t?.tagId ?? t?.id) === String(normalizedTag.tagId)
    );
    if (!exists) {
      item.nutrientGroups[0].selectedTags = [...item.nutrientGroups[0].selectedTags, normalizedTag];
    }

    this.nutrientTagToAdd = null;
    this.refreshNutrientTagOptionsForItem(item);
    this.scheduleLucideIconRender();
  }

  private async loadNutrientTagOptionsForItem(item: any): Promise<void> {
    const groupIds = (item?.nutrientGroups || [])
      .map((g: any) => g?.nutrientGroupId || g?.id)
      .filter((id: any) => id != null && id !== '')
      .map((id: any) => String(id));

    if (!groupIds.length) {
      this.selectedNutrientTagOptions = [];
      return;
    }

    try {
      const tagLists = await Promise.all(
        groupIds.map((groupId: string) => this.getNutrientTagsForGroupCached(groupId))
      );

      const allTags: any[] = [];
      tagLists.forEach((tags) => {
        (tags || []).forEach((t) => {
          allTags.push({ tagId: t.tagId, tagName: t.tagName });
        });
      });

      const uniqueTags = Array.from(
        new Map(allTags.filter((t) => t.tagId != null).map((t) => [String(t.tagId), t])).values()
      );
      this.allNutrientTagOptionsForModal = uniqueTags;
      this.refreshNutrientTagOptionsForItem(item, uniqueTags);
    } catch (error) {
      console.error('Error loading nutrient tag options', error);
      this.selectedNutrientTagOptions = [];
    }
  }

  private refreshNutrientTagOptionsForItem(item: any, allTags?: any[]): void {
    const sourceTags = allTags || this.allNutrientTagOptionsForModal;
    const selectedIds = new Set(
      this.getItemSelectedTags(item).map(t => String(t?.tagId ?? t?.id))
    );
    this.selectedNutrientTagOptions = sourceTags.filter(
      t => !selectedIds.has(String(t?.tagId ?? t?.id))
    );
  }

  private mapGlycemicIndexToCategory(value: any): string {
    if (value === null || value === undefined || value === '') {
      return '';
    }

    const raw = String(value).trim();
    const lower = raw.toLowerCase();
    const byName = (this.glycemicIndexOptions || []).find(
      (o) => String(o?.name || '').trim().toLowerCase() === lower
    );
    if (byName?.name) {
      return byName.name;
    }

    const num = Number(raw);
    if (!Number.isFinite(num) || num <= 0) {
      return '';
    }

    const byCode = (this.glycemicIndexOptions || []).find((o) => Number(o?.code) === num);
    if (byCode?.name) {
      return byCode.name;
    }

    if (num === 1) {
      return 'Low';
    }
    if (num === 2) {
      return 'Medium';
    }
    if (num === 3) {
      return 'High';
    }
    if (num <= 55) {
      return 'Low';
    }
    if (num <= 69) {
      return 'Medium';
    }
    return 'High';
  }

  private mapGlycemicIndexCategoryToApi(value: any): number | null {
    if (value === null || value === undefined || value === '') {
      return null;
    }

    const lower = String(value).trim().toLowerCase();
    const byName = (this.glycemicIndexOptions || []).find(
      (o) => String(o?.name || '').trim().toLowerCase() === lower
    );
    if (byName && Number.isFinite(Number(byName.code)) && Number(byName.code) > 0) {
      return Number(byName.code);
    }

    const num = Number(value);
    return Number.isFinite(num) ? num : null;
  }
}
